import React from 'react';
import { Question, QuestionType, MultipleChoiceQuestion, TrueFalseQuestion, ShortAnswerQuestion } from '../types';

interface QuestionEditorProps {
  question: Question;
  onUpdate: (updates: Partial<Question>) => void;
  onTypeChange: (newType: QuestionType) => void;
  onDone: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  onUpdate,
  onTypeChange,
  onDone,
}) => {
  const addOption = () => {
    if (question.type === 'multiple_choice') {
      const newOption = {
        id: Date.now().toString(),
        text: '',
        isCorrect: false,
      };
      
      onUpdate({
        options: [...(question as MultipleChoiceQuestion).options, newOption],
      } as Partial<MultipleChoiceQuestion>);
    }
  };

  const updateOption = (optionId: string, field: string, value: any) => {
    if (question.type === 'multiple_choice') {
      const updatedOptions = (question as MultipleChoiceQuestion).options.map(option => {
        if (option.id === optionId) {
          return { ...option, [field]: value };
        }
        return option;
      });
      
      onUpdate({
        options: updatedOptions,
      } as Partial<MultipleChoiceQuestion>);
    }
  };

  const deleteOption = (optionId: string) => {
    if (question.type === 'multiple_choice') {
      onUpdate({
        options: (question as MultipleChoiceQuestion).options.filter(option => option.id !== optionId),
      } as Partial<MultipleChoiceQuestion>);
    }
  };

  const setCorrectOption = (optionId: string) => {
    if (question.type === 'multiple_choice') {
      const updatedOptions = (question as MultipleChoiceQuestion).options.map(opt => ({
        ...opt,
        isCorrect: opt.id === optionId
      }));
      onUpdate({ 
        options: updatedOptions 
      } as Partial<MultipleChoiceQuestion>);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <input
            type="text"
            className="w-full text-lg font-medium border-b border-gray-300 pb-1 focus:outline-none focus:border-blue-500"
            value={question.text}
            onChange={(e) => onUpdate({ text: e.target.value })}
            placeholder="Question text"
          />
        </div>
        <div className="ml-4">
          <select
            className="border border-gray-300 rounded-md p-1 text-sm"
            value={question.type}
            onChange={(e) => onTypeChange(e.target.value as QuestionType)}
          >
            <option value="multiple_choice">Multiple Choice</option>
            <option value="true_false">True/False</option>
            <option value="short_answer">Short Answer</option>
          </select>
        </div>
      </div>

      {question.type === 'multiple_choice' && (
        <div className="space-y-2">
          {(question as MultipleChoiceQuestion).options.map((option, optIndex) => (
            <div key={option.id} className="flex items-center space-x-2">
              <input
                type="radio"
                checked={option.isCorrect}
                onChange={() => setCorrectOption(option.id)}
                className="h-4 w-4 text-blue-600"
              />
              <input
                type="text"
                value={option.text}
                onChange={(e) => updateOption(option.id, 'text', e.target.value)}
                className="flex-1 border-b border-gray-300 focus:outline-none focus:border-blue-500 p-1"
                placeholder={`Option ${optIndex + 1}`}
              />
              {(question as MultipleChoiceQuestion).options.length > 2 && (
                <button
                  onClick={() => deleteOption(option.id)}
                  className="text-gray-400 hover:text-red-500"
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addOption}
            className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add option
          </button>
        </div>
      )}

      {question.type === 'true_false' && (
        <div className="space-y-2">
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio h-4 w-4 text-blue-600"
                checked={(question as TrueFalseQuestion).correctAnswer === true}
                onChange={() => onUpdate({ 
                  correctAnswer: true 
                } as Partial<TrueFalseQuestion>)}
              />
              <span className="ml-2">True</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio h-4 w-4 text-blue-600"
                checked={(question as TrueFalseQuestion).correctAnswer === false}
                onChange={() => onUpdate({ 
                  correctAnswer: false 
                } as Partial<TrueFalseQuestion>)}
              />
              <span className="ml-2">False</span>
            </label>
          </div>
        </div>
      )}

      {question.type === 'short_answer' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Correct Answer</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-md p-2"
            value={(question as ShortAnswerQuestion).correctAnswer}
            onChange={(e) => onUpdate({ 
              correctAnswer: e.target.value 
            } as Partial<ShortAnswerQuestion>)}
            placeholder="Enter the correct answer"
          />
        </div>
      )}

      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <div>
          <label className="block text-sm font-medium text-gray-700">Points</label>
          <input
            type="number"
            className="w-20 border border-gray-300 rounded-md p-1 mt-1"
            value={question.points}
            onChange={(e) => onUpdate({ points: parseInt(e.target.value) || 0 })}
            min="0"
          />
        </div>
        <div className="flex space-x-2">
          <button
            onClick={onDone}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
            type="button"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionEditor; 